import { Component } from '@theme/component';

/**
 * Designers A–Z jump navigation: smooth scroll to letter groups and active state sync.
 *
 * @extends {Component}
 */
class DesignersNavComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindJumpLinks();
    this.#observeGroups();
    this.#scrollToHash();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    super.disconnectedCallback();
  }

  #bindJumpLinks() {
    this.querySelectorAll('[data-designers-nav-jump]').forEach((link) => {
      link.addEventListener('click', this.#handleJumpClick);
    });
  }

  /** @param {Event} event */
  #handleJumpClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const targetId = link.dataset.targetId || link.getAttribute('href')?.slice(1);
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${targetId}`);
    this.#setActive(targetId);
  };

  #observeGroups() {
    const groups = this.querySelectorAll('.wf-designers-nav__group');
    if (!groups.length) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]?.target instanceof HTMLElement && visible[0].target.id) {
          this.#setActive(visible[0].target.id);
        }
      },
      {
        root: null,
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.1, 0.25, 0.5],
      },
    );

    groups.forEach((group) => this.#observer?.observe(group));
  }

  /** @param {string} targetId */
  #setActive(targetId) {
    this.querySelectorAll('[data-designers-nav-jump]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;

      const isActive =
        link.dataset.targetId === targetId ||
        link.getAttribute('href') === `#${targetId}`;

      link.toggleAttribute('data-active', isActive);
      if (isActive) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  #scrollToHash() {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const target = document.getElementById(hash);
    if (!target || !this.contains(target)) return;

    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'instant', block: 'start' });
      this.#setActive(hash);
    });
  }
}

if (!customElements.get('designers-nav')) {
  customElements.define('designers-nav', DesignersNavComponent);
}
